import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Density, ThemeMode } from '@/theme/tokens';

interface UiState {
  mode: ThemeMode;
  density: Density;
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  setDensity: (density: Density) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarMobileOpen: (open: boolean) => void;
}

function initialMode(): ThemeMode {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/** User preferences persisted to localStorage (spec §4: persistent preferences). */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      mode: initialMode(),
      density: 'comfortable',
      sidebarCollapsed: false,
      sidebarMobileOpen: false,
      setMode: (mode) => set({ mode }),
      toggleMode: () => set((s) => ({ mode: s.mode === 'dark' ? 'light' : 'dark' })),
      setDensity: (density) => set({ density }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setSidebarMobileOpen: (sidebarMobileOpen) => set({ sidebarMobileOpen }),
    }),
    {
      name: 'agency-admin-ui',
      partialize: (s) => ({
        mode: s.mode,
        density: s.density,
        sidebarCollapsed: s.sidebarCollapsed,
      }),
    },
  ),
);
